import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import BureauCard from "../atoms/BureauCard";
import { dialog } from "../../../components/ui/Dialog";
import { normalizeText } from "../../../utils/text.js";
import {
  INK,
  INK_2,
  INK_3,
  LINE,
  WARN_BG,
  WARN_INK,
  OK_BG,
  OK_INK,
  BAD_BG,
  BAD_INK,
} from "../bureauTokens";

const PAGE_SIZE = 5;

const TONE_STYLES = {
  warn: { bg: WARN_BG, ink: WARN_INK },
  ok: { bg: OK_BG, ink: OK_INK },
  bad: { bg: BAD_BG, ink: BAD_INK },
};

function LmSoumisRowSkeleton() {
  return (
    <div style={{ borderRadius: 9, padding: "8px 9px" }} className="space-y-1.5">
      <div className="h-2.5 w-28 rounded animate-pulse" style={{ background: LINE }} />
      <div className="h-2.5 w-36 rounded animate-pulse" style={{ background: LINE }} />
    </div>
  );
}

// Contrairement à RecapItemRow (bouton cliquable, une seule ligne de sous-
// texte), cette carte n'a aucune action au clic — la demande était
// d'afficher directement le détail (client, type de LM, expert-comptable,
// cabinet, statut) sur la ligne, plus un bouton de suppression dédié.
function LmSoumisRow({ item, onDelete }) {
  const tone = TONE_STYLES[item.tone] || TONE_STYLES.warn;

  const handleDelete = async (e) => {
    e.stopPropagation();
    const confirmed = await dialog.confirm(
      "Voulez-vous vraiment supprimer cette LM soumise ?"
    );
    if (confirmed) onDelete(item.id);
  };

  return (
    <div
      className="w-full grid items-start"
      style={{
        gridTemplateColumns: "1fr auto",
        gap: "2px 8px",
        background: tone.bg,
        borderRadius: 9,
        padding: "7px 9px",
      }}
    >
      <span className="text-[10px] font-semibold truncate" style={{ color: INK }}>
        {item.client}
      </span>
      <span
        className="text-[9px] font-bold whitespace-nowrap"
        style={{ color: tone.ink }}
      >
        {item.statutLabel}
      </span>
      <span className="text-[9px] truncate" style={{ color: INK_2 }}>
        {item.docType}
        {item.comptable ? ` — ${item.comptable}` : ""}
        {item.cabinet ? ` — ${item.cabinet}` : ""}
      </span>
      <button
        type="button"
        onClick={handleDelete}
        title="Supprimer cette LM soumise"
        className="justify-self-end text-slate-300 hover:text-red-500 transition-colors"
        style={{ padding: 2 }}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

function LmSoumisCard({ items, loading, onDelete }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = normalizeText(search.trim());
    if (!q) return items;
    return items.filter((item) =>
      normalizeText(
        `${item.client} ${item.docType} ${item.comptable || ""} ${item.cabinet || ""}`
      ).includes(q)
    );
  }, [items, search]);

  // items arrive déjà triés (le plus récent créé/modifié en premier, voir
  // Bureau.jsx#lmSoumisItems) — pas de re-tri ici, juste filtre + pagination.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, items.length]);

  return (
    <BureauCard>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-semibold whitespace-nowrap" style={{ color: INK }}>
          LM Soumis
        </div>
        <div className="relative">
          <Search
            size={10}
            className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: INK_3 }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="text-[9.5px] rounded-full outline-none w-24 focus:w-32 transition-all"
            style={{
              border: `1px solid ${LINE}`,
              padding: "3px 8px 3px 18px",
              color: INK,
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <LmSoumisRowSkeleton key={i} />)
        ) : (
          <>
            {pageItems.map((item) => (
              <LmSoumisRow key={item.id} item={item} onDelete={onDelete} />
            ))}
            {filtered.length === 0 && (
              <div className="py-6 text-center text-xs" style={{ color: INK_3 }}>
                {items.length === 0 ? "Aucune LM soumise" : "Aucun résultat"}
              </div>
            )}
          </>
        )}
      </div>
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: `1px solid ${LINE}` }}>
          <span className="text-[9px]" style={{ color: INK_3 }}>
            Page {page}/{totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="w-5 h-5 flex items-center justify-center rounded-md disabled:opacity-30 transition-colors"
              style={{ border: `1px solid ${LINE}`, color: INK_2 }}
            >
              <ChevronLeft size={11} />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="w-5 h-5 flex items-center justify-center rounded-md disabled:opacity-30 transition-colors"
              style={{ border: `1px solid ${LINE}`, color: INK_2 }}
            >
              <ChevronRight size={11} />
            </button>
          </div>
        </div>
      )}
    </BureauCard>
  );
}

export default LmSoumisCard;
